/** @format */

import {
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Avatar,
	Grid,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { CheckCircle } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { XCircle } from "@phosphor-icons/react/dist/ssr/XCircle";
import { Trash } from "@phosphor-icons/react/dist/ssr/Trash";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import InfoIcon from "@mui/icons-material/Info";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GavelIcon from "@mui/icons-material/Gavel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	fetchAllUnionLeaderApplications,
	approveApplication,
	rejectApplication,
	deleteApplication,
} from "../../../redux/actions/circleAction";
import {
	selectUnionLeaderApplications,
	selectIsLoadingApplications,
	selectIsApprovingApplication,
	selectIsRejectingApplication,
	selectIsDeletingApplication,
} from "../../../redux/selectors/circleSelector";
import {
	LoadingSpinner,
	SuccessNotification,
} from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";

interface UnionLeaderApplication {
	_id: string;
	userId:
		| {
				_id: string;
				firstName: string;
				lastName: string;
				email: string;
				profileImage?: string;
		  }
		| string; // Can be populated object or just string ID
	email: string;
	reason: string;
	status: "Pending" | "Approved" | "Rejected";
	hasAgreedToTerms: boolean;
	created_At: string;
	updated_At: string;
}

const CircleLeaderApplications = () => {
	const dispatch = useDispatch<AppDispatch>();
	const applications = useSelector(selectUnionLeaderApplications);
	const isLoading = useSelector(selectIsLoadingApplications);
	const isApproving = useSelector(selectIsApprovingApplication);
	const isRejecting = useSelector(selectIsRejectingApplication);
	const isDeleting = useSelector(selectIsDeletingApplication);
	const [openNav, setOpenNav] = useState(false);
	// Track which specific application is being processed
	const [processingAppId, setProcessingAppId] = useState<string | null>(null);
	// Track if modal action is processing
	const [isProcessing, setIsProcessing] = useState(false);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		type: "approve" | "reject" | "delete" | null;
		applicationId: string | null;
		applicantName: string;
	}>({
		open: false,
		type: null,
		applicationId: null,
		applicantName: "",
	});
	const [detailsModal, setDetailsModal] = useState<{
		open: boolean;
		application: UnionLeaderApplication | null;
	}>({
		open: false,
		application: null,
	});
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});

	// Fetch data when status filter changes (including initial mount)
	useEffect(() => {
		const statusParam = statusFilter === "all" ? undefined : statusFilter;
		dispatch(fetchAllUnionLeaderApplications(statusParam));
	}, [statusFilter, dispatch]);

	const handleRefresh = async () => {
		try {
			const statusParam = statusFilter === "all" ? undefined : statusFilter;
			await dispatch(fetchAllUnionLeaderApplications(statusParam)).unwrap();
			setNotification({
				open: true,
				message: "Applications refreshed successfully",
				severity: "success",
			});
		} catch (err: any) {
			setNotification({
				open: true,
				message:
					err?.message || err?.toString() || "Failed to refresh applications",
				severity: "error",
			});
		}
	};

	const handleOpenConfirmDialog = (
		type: "approve" | "reject" | "delete",
		applicationId: string,
		applicantName: string,
	) => {
		setConfirmDialog({
			open: true,
			type,
			applicationId,
			applicantName,
		});
	};

	const handleCloseConfirmDialog = () => {
		// Clear processing states when closing dialog
		setProcessingAppId(null);
		setIsProcessing(false);
		setConfirmDialog({
			open: false,
			type: null,
			applicationId: null,
			applicantName: "",
		});
	};

	const handleOpenDetailsModal = (application: UnionLeaderApplication) => {
		setDetailsModal({
			open: true,
			application,
		});
	};

	const handleCloseDetailsModal = () => {
		setDetailsModal({
			open: false,
			application: null,
		});
	};

	const handleConfirmAction = async () => {
		const { type, applicationId } = confirmDialog;
		if (!type || !applicationId) return;

		// Set the processing states
		setProcessingAppId(applicationId);
		setIsProcessing(true);

		try {
			let message = "";
			if (type === "approve") {
				await dispatch(approveApplication(applicationId)).unwrap();
				message = "Application approved successfully";
			} else if (type === "reject") {
				await dispatch(rejectApplication(applicationId)).unwrap();
				message = "Application rejected successfully";
			} else if (type === "delete") {
				await dispatch(deleteApplication(applicationId)).unwrap();
				message = "Application deleted successfully";
			}

			// Refresh the applications list after successful action
			const statusParam = statusFilter === "all" ? undefined : statusFilter;
			await dispatch(fetchAllUnionLeaderApplications(statusParam)).unwrap();

			setNotification({
				open: true,
				message,
				severity: "success",
			});
			handleCloseConfirmDialog();
		} catch (err: any) {
			// Clear processing states on error
			setProcessingAppId(null);
			setIsProcessing(false);
			setNotification({
				open: true,
				message:
					err?.message || err?.toString() || `Failed to ${type} application`,
				severity: "error",
			});
		}
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleCloseNotification = () => {
		setNotification((prev) => ({ ...prev, open: false }));
	};

	// Filter applications based on search only (status filtering is done server-side)
	const filteredApplications = (applications || []).filter(
		(app: UnionLeaderApplication) => {
			if (searchQuery === "") return true;

			const searchLower = searchQuery.toLowerCase();
			const emailMatch = app.email?.toLowerCase().includes(searchLower);

			// Check if userId is populated with user details
			if (typeof app.userId === "object") {
				const firstNameMatch = app.userId.firstName
					?.toLowerCase()
					.includes(searchLower);
				const lastNameMatch = app.userId.lastName
					?.toLowerCase()
					.includes(searchLower);
				return emailMatch || firstNameMatch || lastNameMatch;
			}

			return emailMatch;
		},
	);

	const paginatedApplications = filteredApplications.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const getStatusColor = (
		status: string,
	): "default" | "success" | "error" | "warning" => {
		switch (status?.toLowerCase()) {
			case "approved":
				return "success";
			case "rejected":
				return "error";
			case "pending":
				return "warning";
			default:
				return "default";
		}
	};

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{isLoading ?
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={isLoading} size={45} />
						</Box>
					:	<>
							<Stack spacing={3}>
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">
											Circle Leader Applications
										</Typography>
										<Typography color="text.secondary" variant="body2">
											Review and manage circle leader applications
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Tooltip title="Refresh">
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												<ArrowsClockwise />
											</IconButton>
										</Tooltip>
									</Box>
								</Stack>
								{/* Stats Cards */}
								<Stack direction="row" spacing={2}>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="primary">
											{applications.length}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Total Applications
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="warning.main">
											{
												applications.filter((a) => a.status === "Pending")
													.length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Pending
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="success.main">
											{
												applications.filter((a) => a.status === "Approved")
													.length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Approved
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="error.main">
											{
												applications.filter((a) => a.status === "Rejected")
													.length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Rejected
										</Typography>
									</Card>
								</Stack>
								{/* Filters and Search */}
								<Card>
									<Stack
										direction="row"
										spacing={2}
										sx={{ p: 2 }}
										alignItems="center">
										<OutlinedInput
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											fullWidth
											placeholder="Search by name or email..."
											startAdornment={
												<InputAdornment position="start">
													<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
												</InputAdornment>
											}
										/>
										<Stack direction="row" spacing={1}>
											<Button
												variant={
													statusFilter === "all" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("all")}
												size="small">
												All
											</Button>
											<Button
												variant={
													statusFilter === "pending" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("pending")}
												size="small"
												color="warning">
												Pending
											</Button>
											<Button
												variant={
													statusFilter === "approved" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("approved")}
												size="small"
												color="success">
												Approved
											</Button>
											<Button
												variant={
													statusFilter === "rejected" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("rejected")}
												size="small"
												color="error">
												Rejected
											</Button>
										</Stack>
									</Stack>
								</Card>
								<Card>
									{isLoading ?
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												minHeight: 400,
											}}>
											<CircularProgress />
										</Box>
									:	<>
											<Box sx={{ overflowX: "auto" }}>
												<Table sx={{ minWidth: "800px" }}>
													<TableHead>
														<TableRow>
															<TableCell>Applicant</TableCell>
															<TableCell>Email</TableCell>
															<TableCell>Status</TableCell>
															<TableCell>Applied Date</TableCell>
															<TableCell align="center">More Info</TableCell>
															<TableCell align="center">Actions</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{paginatedApplications.length === 0 ?
															<TableRow>
																<TableCell colSpan={7} align="center">
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		No applications found
																	</Typography>
																</TableCell>
															</TableRow>
														:	paginatedApplications.map(
																(app: UnionLeaderApplication) => (
																	<TableRow hover key={app._id}>
																		<TableCell>
																			<Stack
																				direction="row"
																				spacing={2}
																				alignItems="center">
																				<Avatar
																					src={
																						typeof app.userId === "object" ?
																							app.userId.profileImage
																						:	undefined
																					}
																					sx={{ width: 40, height: 40 }}>
																					{typeof app.userId === "object" ?
																						`${app.userId.firstName?.[0]}${app.userId.lastName?.[0]}`
																					:	"?"}
																				</Avatar>
																				<Typography variant="subtitle2">
																					{typeof app.userId === "object" ?
																						`${app.userId.firstName} ${app.userId.lastName}`
																					:	`User: ${app.userId}`}
																				</Typography>
																			</Stack>
																		</TableCell>
																		<TableCell>{app.email}</TableCell>
																		<TableCell>
																			<Chip
																				label={app.status}
																				color={getStatusColor(app.status)}
																				size="small"
																			/>
																		</TableCell>
																		<TableCell>
																			{dayjs(app.created_At).format(
																				"MMM D, YYYY",
																			)}
																		</TableCell>
																		<TableCell align="center">
																			<Tooltip title="More Info">
																				<IconButton
																					color="primary"
																					size="small"
																					onClick={() =>
																						handleOpenDetailsModal(app)
																					}>
																					<InfoIcon />
																				</IconButton>
																			</Tooltip>
																		</TableCell>
																		<TableCell>
																			<Stack
																				direction="row"
																				spacing={1}
																				justifyContent="center">
																				{app.status === "Pending" && (
																					<>
																						<Tooltip title="Approve">
																							<IconButton
																								color="success"
																								size="small"
																								disabled={
																									(isApproving ||
																										isRejecting) &&
																									processingAppId === app._id
																								}
																								onClick={() =>
																									handleOpenConfirmDialog(
																										"approve",
																										app._id,
																										(
																											typeof app.userId ===
																												"object"
																										) ?
																											`${app.userId.firstName} ${app.userId.lastName}`
																										:	app.email,
																									)
																								}>
																								{(
																									isApproving &&
																									processingAppId === app._id
																								) ?
																									<CircularProgress size={20} />
																								:	<CheckCircle />}
																							</IconButton>
																						</Tooltip>
																						<Tooltip title="Reject">
																							<IconButton
																								color="error"
																								size="small"
																								disabled={
																									(isApproving ||
																										isRejecting) &&
																									processingAppId === app._id
																								}
																								onClick={() =>
																									handleOpenConfirmDialog(
																										"reject",
																										app._id,
																										(
																											typeof app.userId ===
																												"object"
																										) ?
																											`${app.userId.firstName} ${app.userId.lastName}`
																										:	app.email,
																									)
																								}>
																								{(
																									isRejecting &&
																									processingAppId === app._id
																								) ?
																									<CircularProgress size={20} />
																								:	<XCircle />}
																							</IconButton>
																						</Tooltip>
																					</>
																				)}
																				<Tooltip title="Delete">
																					<IconButton
																						color="error"
																						size="small"
																						disabled={
																							isDeleting &&
																							processingAppId === app._id
																						}
																						onClick={() =>
																							handleOpenConfirmDialog(
																								"delete",
																								app._id,
																								typeof app.userId === "object" ?
																									`${app.userId.firstName} ${app.userId.lastName}`
																								:	app.email,
																							)
																						}>
																						{(
																							isDeleting &&
																							processingAppId === app._id
																						) ?
																							<CircularProgress size={20} />
																						:	<Trash />}
																					</IconButton>
																				</Tooltip>
																			</Stack>
																		</TableCell>
																	</TableRow>
																),
															)
														}
													</TableBody>
												</Table>
											</Box>
											<Divider />
											<TablePagination
												component="div"
												count={filteredApplications.length}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
												page={page}
												rowsPerPage={rowsPerPage}
												rowsPerPageOptions={[5, 10, 25]}
											/>
										</>
									}
								</Card>
								{/* Confirmation Dialog */}
								<Dialog
									open={confirmDialog.open}
									onClose={!isProcessing ? handleCloseConfirmDialog : undefined}
									aria-labelledby="confirm-dialog-title">
									<DialogTitle id="confirm-dialog-title">
										{confirmDialog.type === "approve" && "Approve Application"}
										{confirmDialog.type === "reject" && "Reject Application"}
										{confirmDialog.type === "delete" && "Delete Application"}
									</DialogTitle>
									<DialogContent>
										{isProcessing ?
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													gap: 2,
													py: 3,
												}}>
												<CircularProgress size={10} />
												<Typography variant="body2" color="text.secondary">
													Processing...
												</Typography>
											</Box>
										:	<DialogContentText>
												{confirmDialog.type === "approve" &&
													`Are you sure you want to approve the application from ${confirmDialog.applicantName}? This will grant them union leader privileges.`}
												{confirmDialog.type === "reject" &&
													`Are you sure you want to reject the application from ${confirmDialog.applicantName}?`}
												{confirmDialog.type === "delete" &&
													`Are you sure you want to delete the application from ${confirmDialog.applicantName}? This action cannot be undone.`}
											</DialogContentText>
										}
									</DialogContent>
									<DialogActions>
										<Button
											onClick={handleCloseConfirmDialog}
											color="inherit"
											disabled={isProcessing}>
											Cancel
										</Button>
										<Button
											onClick={handleConfirmAction}
											color={
												confirmDialog.type === "approve" ? "success" : "error"
											}
											variant="contained"
											disabled={isProcessing}
											autoFocus>
											{confirmDialog.type === "approve" && "Approve"}
											{confirmDialog.type === "reject" && "Reject"}
											{confirmDialog.type === "delete" && "Delete"}
										</Button>
									</DialogActions>
								</Dialog>{" "}
								{/* Application Details Modal */}
								<Dialog
									open={detailsModal.open}
									onClose={handleCloseDetailsModal}
									maxWidth="md"
									fullWidth
									aria-labelledby="details-dialog-title"
									PaperProps={{
										sx: {
											borderRadius: 3,
											overflow: "hidden",
										},
									}}>
									<DialogTitle
										id="details-dialog-title"
										sx={{
											background:
												"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
											color: "white",
											py: 2.5,
										}}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											<Typography variant="h5" fontWeight={600}>
												Application Details
											</Typography>
											<IconButton
												size="small"
												sx={{ color: "white" }}
												onClick={handleCloseDetailsModal}>
												<CloseIcon />
											</IconButton>
										</Stack>
									</DialogTitle>
									<DialogContent sx={{ p: 0 }}>
										{detailsModal.application && (
											<Box>
												{/* Applicant Profile Header */}
												<Box
													sx={{
														background:
															"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
														pt: 0,
														pb: 8,
														px: 3,
													}}>
													<Stack
														direction="row"
														spacing={3}
														alignItems="center">
														<Avatar
															src={
																(
																	typeof detailsModal.application.userId ===
																	"object"
																) ?
																	detailsModal.application.userId.profileImage
																:	undefined
															}
															sx={{
																width: 80,
																height: 80,
																border: "4px solid white",
																boxShadow: 3,
															}}>
															{(
																typeof detailsModal.application.userId ===
																"object"
															) ?
																`${detailsModal.application.userId.firstName?.[0]}${detailsModal.application.userId.lastName?.[0]}`
															:	"?"}
														</Avatar>
														<Box>
															<Typography
																variant="h5"
																fontWeight={600}
																color="white">
																{(
																	typeof detailsModal.application.userId ===
																	"object"
																) ?
																	`${detailsModal.application.userId.firstName} ${detailsModal.application.userId.lastName}`
																:	`User ID: ${detailsModal.application.userId}`}
															</Typography>
															<Typography
																variant="body2"
																sx={{
																	color: "rgba(255,255,255,0.85)",
																	mt: 0.5,
																}}>
																{detailsModal.application.email}
															</Typography>
														</Box>
													</Stack>
												</Box>

												{/* Status Cards - Overlapping Design */}
												<Box sx={{ px: 3, mt: -5 }}>
													<Grid container spacing={2}>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		(
																			detailsModal.application.status ===
																			"Approved"
																		) ?
																			"linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
																		: (
																			detailsModal.application.status ===
																			"Rejected"
																		) ?
																			"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)"
																		:	"linear-gradient(135deg, #f5af19 0%, #f12711 100%)",
																	color: "white",
																}}>
																<VerifiedUserIcon
																	sx={{ fontSize: 32, mb: 1 }}
																/>
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Application Status
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.application.status}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		detailsModal.application.hasAgreedToTerms ?
																			"linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
																		:	"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
																	color: "white",
																}}>
																<GavelIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Terms & Conditions
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.application.hasAgreedToTerms ?
																		"Agreed"
																	:	"Not Agreed"}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
																	color: "white",
																}}>
																<CalendarTodayIcon
																	sx={{ fontSize: 32, mb: 1 }}
																/>
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Applied On
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{dayjs(
																		detailsModal.application.created_At,
																	).format("MMM D, YYYY")}
																</Typography>
															</Card>
														</Grid>
													</Grid>
												</Box>

												{/* Reason for Application Section */}
												<Box sx={{ px: 3, py: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Reason for Application
													</Typography>
													<Card
														variant="outlined"
														sx={{
															borderRadius: 2,
															overflow: "hidden",
															p: 2.5,
															backgroundColor: "grey.50",
														}}>
														<Typography
															variant="body1"
															sx={{
																fontStyle: "italic",
																color: "text.primary",
															}}>
															"
															{detailsModal.application.reason ||
																"No reason provided"}
															"
														</Typography>
													</Card>
												</Box>

												{/* Application Information Section */}
												<Box sx={{ px: 3, pb: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Application Information
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Stack divider={<Divider />}>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Application ID
																</Typography>
																<Typography
																	variant="body2"
																	fontWeight={500}
																	sx={{ fontFamily: "monospace" }}>
																	{detailsModal.application._id}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Email Address
																</Typography>
																<Typography variant="body2" fontWeight={500}>
																	{detailsModal.application.email}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Status
																</Typography>
																<Chip
																	label={detailsModal.application.status}
																	color={getStatusColor(
																		detailsModal.application.status,
																	)}
																	size="small"
																/>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Terms Agreement
																</Typography>
																<Chip
																	label={
																		detailsModal.application.hasAgreedToTerms ?
																			"Agreed"
																		:	"Not Agreed"
																	}
																	color={
																		detailsModal.application.hasAgreedToTerms ?
																			"success"
																		:	"error"
																	}
																	size="small"
																/>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Submitted On
																</Typography>
																<Typography variant="body2" fontWeight={500}>
																	{dayjs(
																		detailsModal.application.created_At,
																	).format("MMMM D, YYYY [at] h:mm A")}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Last Updated
																</Typography>
																<Typography variant="body2" fontWeight={500}>
																	{dayjs(
																		detailsModal.application.updated_At,
																	).format("MMMM D, YYYY [at] h:mm A")}
																</Typography>
															</Stack>
														</Stack>
													</Card>
												</Box>

												{/* Action Buttons for Pending Applications */}
												{detailsModal.application.status === "Pending" && (
													<Box sx={{ px: 3, pb: 3 }}>
														<Stack
															direction="row"
															spacing={2}
															justifyContent="center">
															<Button
																variant="contained"
																color="success"
																startIcon={<CheckCircle />}
																onClick={() => {
																	const app = detailsModal.application;
																	if (!app) return;
																	handleCloseDetailsModal();
																	handleOpenConfirmDialog(
																		"approve",
																		app._id,
																		typeof app.userId === "object" ?
																			`${app.userId.firstName} ${app.userId.lastName}`
																		:	app.email,
																	);
																}}
																sx={{ px: 4, py: 1.5, borderRadius: 2 }}>
																Approve
															</Button>
															<Button
																variant="contained"
																color="error"
																startIcon={<XCircle />}
																onClick={() => {
																	const app = detailsModal.application;
																	if (!app) return;
																	handleCloseDetailsModal();
																	handleOpenConfirmDialog(
																		"reject",
																		app._id,
																		typeof app.userId === "object" ?
																			`${app.userId.firstName} ${app.userId.lastName}`
																		:	app.email,
																	);
																}}
																sx={{ px: 4, py: 1.5, borderRadius: 2 }}>
																Reject
															</Button>
														</Stack>
													</Box>
												)}
											</Box>
										)}
									</DialogContent>
									<DialogActions sx={{ px: 3, py: 2 }}>
										<Button
											onClick={handleCloseDetailsModal}
											variant="outlined"
											sx={{ borderRadius: 2 }}>
											Close
										</Button>
									</DialogActions>
								</Dialog>
								{/* Notification */}
								<SuccessNotification
									open={notification.open}
									onClose={handleCloseNotification}
									severity={notification.severity}
									message={notification.message}
								/>
							</Stack>
						</>
					}
				</Main>
			</Box>
		</React.Fragment>
	);
};

export default CircleLeaderApplications;
